import React, { useState, useEffect, useRef } from 'react';
import { QrReader } from 'react-qr-reader';
import { Card, CardContent, Typography, Box, Alert, Button, Tabs, Tab, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import PersistentDrawerLeft from '../../components/NewDrawer';
import DinoDialog from '../../components/Dialog';
import useCallDataApi from "../../hooks/data";
import useDataModel from "../../hooks/dataModel";
import {extractSlugFromUrl} from "../../utils/utils";
import {format} from "date-fns";
import jsQR from 'jsqr';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

const CourierScanner = () => {
    const [hasCamera, setHasCamera] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [scannedData, setScannedData] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [isProcessingImage, setIsProcessingImage] = useState(false);
    const fileInputRef = useRef(null);
    const {postModel} = useDataModel(useCallDataApi('orders'))
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        // Check if device has camera
        navigator?.mediaDevices?.getUserMedia({ video: true })
            .then(() => {
                setHasCamera(true);
            })
            .catch(() => {
                setHasCamera(false);
                enqueueSnackbar('Kamera nem elérhető ezen az eszközön', { variant: 'error' });
            });
    }, [enqueueSnackbar]);

    const handleScan = (url) => {
        console.log(`scanned url: ${url}`);
        console.log(`scannedData: ${scannedData}`);
        if (!scannedData && !!url) {
            console.log(`!scannedData && !!url: ${!scannedData && !!url}`);
            setScannedData(url)
            try {
                const slug = extractSlugFromUrl(url);
                console.log(`slug: ${slug}`);
                postModel('get_order_number_by_slug', {slug: slug}, handleOrder)
            } catch (e) {
                console.log('Error in try')
                setScannedData(null)
            }
        }
    }

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Check if file is an image
        if (!file.type.startsWith('image/')) {
            enqueueSnackbar('Kérjük, csak képeket töltsön fel!', { variant: 'error' });
            return;
        }

        setIsProcessingImage(true);
        const reader = new FileReader();
        
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                try {
                    // Create canvas to process the image
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    
                    // Draw image on canvas
                    ctx.drawImage(img, 0, 0);
                    
                    // Get image data for QR processing
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    
                    // Process QR code using jsQR
                    const code = jsQR(imageData.data, imageData.width, imageData.height);
                    
                    if (code) {
                        console.log('QR code found in image:', code.data);
                        handleScan(code.data);
                    } else {
                        enqueueSnackbar('Nem található QR kód a feltöltött képen', { variant: 'warning' });
                    }
                } catch (error) {
                    console.error('Error processing image:', error);
                    enqueueSnackbar('Hiba történt a kép feldolgozása során', { variant: 'error' });
                } finally {
                    setIsProcessingImage(false);
                    // Reset file input
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                }
            };
            
            img.onerror = () => {
                enqueueSnackbar('Hiba történt a kép betöltése során', { variant: 'error' });
                setIsProcessingImage(false);
            };
            
            img.src = e.target.result;
        };
        
        reader.onerror = () => {
            enqueueSnackbar('Hiba történt a fájl olvasása során', { variant: 'error' });
            setIsProcessingImage(false);
        };
        
        reader.readAsDataURL(file);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setScannedData(null); // Reset scanned data when switching tabs
    };

    const handleOrder = order => {
        console.log(`handleOrder: ${order}`);
        if (order) {
            console.log('order found')
            const today = new Date()
            if (order?.actual_delivery_date !== format(today, 'yyyy-MM-dd')) enqueueSnackbar('Nem ma kellene kiszállítani a rendelést!', {variant: 'warning'})
            setSelectedOrder(order)
            setDialogOpen(true)
        }
    };

    const handleError = (error) => {
        console.error('QR Scanner Error:', error);
    };

    const handleDialogClose = () => {
        setDialogOpen(false)
        setScannedData(null)
    }

    const pickUp = id => {
        postModel('courier_pickup', {id: id}, handleOrder)
    }

    return (<PersistentDrawerLeft title='Csomagfelvétel'>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    QR Kód Olvasó
                </Typography>
                
                {!scannedData && (
                    <Card>
                        <CardContent>
                            <Tabs value={activeTab} onChange={handleTabChange} centered sx={{ mb: 2 }}>
                                <Tab 
                                    icon={<CameraAltIcon />} 
                                    label="Kamera" 
                                    iconPosition="start"
                                />
                                <Tab 
                                    icon={<UploadFileIcon />} 
                                    label="Kép feltöltés" 
                                    iconPosition="start"
                                />
                            </Tabs>
                            
                            {activeTab === 0 && (
                                <Box sx={{width: '100%', maxWidth: 500, mx: 'auto'}}>
                                    <QrReader
                                        constraints={{facingMode: 'environment'}}
                                        onResult={handleScan}
                                        onError={handleError}
                                        style={{width: '100%'}}
                                    />
                                    <Typography variant="body1" sx={{mt: 2, textAlign: 'center'}}>
                                        Tartsa a kamerát a QR kód elé
                                    </Typography>
                                </Box>
                            )}
                            
                            {activeTab === 1 && (
                                <Box sx={{width: '100%', maxWidth: 500, mx: 'auto', textAlign: 'center'}}>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                        id="image-upload"
                                    />
                                    <label htmlFor="image-upload">
                                        <Button
                                            variant="outlined"
                                            component="span"
                                            startIcon={<UploadFileIcon />}
                                            disabled={isProcessingImage}
                                            sx={{ mb: 2 }}
                                        >
                                            {isProcessingImage ? 'Feldolgozás...' : 'Kép kiválasztása'}
                                        </Button>
                                    </label>
                                    <Typography variant="body1" sx={{mt: 2, textAlign: 'center'}}>
                                        Töltse fel a QR kódot tartalmazó képet
                                    </Typography>
                                    <Typography variant="caption" sx={{mt: 1, textAlign: 'center', display: 'block', color: 'text.secondary'}}>
                                        Támogatott formátumok: JPG, PNG, GIF, WebP
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}
            </Box>

            <DinoDialog
                open={dialogOpen}
                handleClose={handleDialogClose}
                title="QR Kód Beolvasva"
                subtitle="A következő azonosító került beolvasásra"
                actions={
                    <Button onClick={() => {
                        pickUp(selectedOrder?.id)
                        handleDialogClose()
                    }} variant="contained" color="primary">
                        Felvétel
                    </Button>
                }
            >
                <Typography variant="h6">
                    Rendelés szám: {selectedOrder?.order_number}
                </Typography>
            </DinoDialog>

        </PersistentDrawerLeft>
    );
};

export default CourierScanner;